// frontend/src/components/MetaCubeOracleV5.jsx
import React, { useRef, useState, useEffect, useMemo } from "react";
import * as THREE from "three";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import { EffectComposer, Bloom } from "@react-three/postprocessing";
import { MeshLineGeometry, MeshLineMaterial } from "meshline";
import "../styles/metacube_oracle_v5.css";

function MetaCubeOracleV5Core({
  running = true,
  chaos = false,
  glow = true,
  particleDensityPct = 60,
  chaoticSpeedPct = 50,
  spectralIntensityPct = 40,
  bloomIntensity = 1.2,
  bloomRadius = 0.6,
  bloomThreshold = 0.0,
  onEntropyUpdate = () => {},
  onStatsUpdate = () => {},
}) {
  const groupRef = useRef();
  const particlesRef = useRef();

  const [time, setTime] = useState(0);
  const [entropy, setEntropy] = useState({ shannon: 0, accumulator: 0 });

  // état de l’éclair
  const [lightningPaths, setLightningPaths] = useState([]);
  const [lightningGeoms, setLightningGeoms] = useState([null, null]);
  const [lightningHaloGeom, setLightningHaloGeom] = useState(null);
  const [lightningOpacity, setLightningOpacity] = useState(0);
  const [lightningIntensity, setLightningIntensity] = useState(1.0);
  const lightningStartRef = useRef(null);

  // --- Particules initiales ---
  const { positions, baseColors, alphas, particleCount } = useMemo(() => {
    const count = Math.floor(800 + (particleDensityPct / 100) * 2000);
    const pos = new Float32Array(count * 3);
    const col = new Float32Array(count * 3);
    const alpha = new Float32Array(count).fill(1.0);

    for (let i = 0; i < count; i++) {
      pos[i * 3] = (Math.random() - 0.5) * 120;
      pos[i * 3 + 1] = (Math.random() - 0.5) * 120;
      pos[i * 3 + 2] = (Math.random() - 0.5) * 120;
      const isPositive = Math.random() > 0.5;
      const color = new THREE.Color(
        isPositive ? "hsl(0,100%,60%)" : "hsl(200,100%,60%)"
      );
      col[i * 3] = color.r;
      col[i * 3 + 1] = color.g;
      col[i * 3 + 2] = color.b;
    }

    return { positions: pos, baseColors: col, alphas: alpha, particleCount: count };
  }, [particleDensityPct]);

  // --- Génère un éclair ---
  function generateLightningPath(start, segments = 12, step = 20) {
    const points = [];
    let current = new THREE.Vector3(...start);
    for (let i = 0; i < segments; i++) {
      const next = current.clone().add(
        new THREE.Vector3(
          (Math.random() - 0.5) * step,
          -step,
          (Math.random() - 0.5) * step
        )
      );
      points.push(next.clone());
      current = next;
    }
    return points;
  }

  // --- Backend events ---
  useEffect(() => {
    let active = true;
    let intervalId;

    const fetchEntropy = async () => {
      try {
        const res = await fetch("/api/geometry/metacube_oracle_v5/animate", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            particle_density: particleDensityPct / 100,
            chaotic_speed: chaoticSpeedPct / 100,
            spectral_intensity: spectralIntensityPct / 100,
            chaos,
            glow,
            running,
            bloom_intensity: bloomIntensity,
            bloom_radius: bloomRadius,
            bloom_threshold: bloomThreshold,
          }),
        });
        const data = await res.json();
        if (!active) return;

        // ⚠️ LA VALEUR AFFICHÉE VIENT DÉSORMAIS DE `/dyn_entropy`.
        //
        // `/animate` est CONSERVÉ tel quel : c'est lui qui déclenche les
        // éclairs et remonte les compteurs de particules, donc le supprimer
        // retirerait un effet visible. Mais sa `shannonEntropy` était une
        // fonction de curseurs, et le backend a cessé le 2026-07-29 d'en
        // écrire la métrique — la page montrait un nombre mesuré nulle part.
        //
        // On lit la mesure à côté, sans toucher au reste de la réponse.
        try {
          const mes = await fetch("/api/geometry/metacube_oracle_v5/dyn_entropy", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: "{}",
          });
          const m = await mes.json();
          if (active && typeof m.entropy_dyn === "number") {
            const val = { shannon: m.entropy_dyn, accumulator: m.entropy_dyn };
            setEntropy(val);
            onEntropyUpdate(val);
          }
        } catch (e) {
          /* la mesure est indisponible : on n'affiche pas de valeur inventée */
        }

        if (data.activeCount !== undefined && data.destroyedCount !== undefined) {
          onStatsUpdate({
            activeCount: data.activeCount,
            destroyedCount: data.destroyedCount,
            lightning:
              data.event === "lightning"
                ? {
                    charge: data.charge,
                    destroyed: data.destroyedParticles,
                  }
                : null,
          });
        }

        // ⚡ Éclair reçu
        if (data.event === "lightning" && data.destroyedParticles > 0) {
          const destroyed = data.destroyedParticles || 0;
          const intensity = Math.min(1.0, destroyed / 50);
          setLightningIntensity(intensity);

          // son zap
          try {
            const audio = new Audio("/sounds/zap.mp3");
            audio.volume = 0.4 + 0.6 * intensity;
            audio.play().catch(() => {});
          } catch {}

          const starts = [
            [(Math.random() - 0.5) * 100, (Math.random() - 0.5) * 100, (Math.random() - 0.5) * 100],
            [(Math.random() - 0.5) * 100, (Math.random() - 0.5) * 100, (Math.random() - 0.5) * 100],
          ];
          const paths = [generateLightningPath(starts[0]), generateLightningPath(starts[1])];
          setLightningPaths(paths);

          const geoms = paths.map((path) => {
            const geom = new MeshLineGeometry();
            geom.setPoints(path.map((p) => [p.x, p.y, p.z]).flat());
            return geom;
          });
          setLightningGeoms(geoms);

          const haloGeom = new MeshLineGeometry();
          haloGeom.setPoints(paths[0].map((p) => [p.x, p.y, p.z]).flat());
          setLightningHaloGeom(haloGeom);

          setLightningOpacity(1.0);
          lightningStartRef.current = performance.now();
        }
      } catch (err) {
        console.warn("MetaCubeOracleV5 backend error:", err);
      }
    };

    if (running) {
      fetchEntropy();
      intervalId = setInterval(fetchEntropy, 2000);
    }

    return () => {
      active = false;
      if (intervalId) clearInterval(intervalId);
    };
  }, [
    running,
    particleDensityPct,
    chaoticSpeedPct,
    spectralIntensityPct,
    chaos,
    glow,
    bloomIntensity,
    bloomRadius,
    bloomThreshold,
    onEntropyUpdate,
    onStatsUpdate,
  ]);

  // --- Animation frame ---
  useFrame((_, delta) => {
    if (!running || !groupRef.current || !particlesRef.current) return;

    setTime((t) => t + delta);
    const g = groupRef.current;
    const geom = particlesRef.current.geometry;
    const cols = geom.attributes.color.array;

    for (let i = 0; i < particleCount; i++) {
      const hue =
        ((i / particleCount) * 360 + spectralIntensityPct * 3.6 + time * 20) % 360;
      const color = new THREE.Color(`hsl(${hue},100%,60%)`);
      cols[i * 3] = color.r;
      cols[i * 3 + 1] = color.g;
      cols[i * 3 + 2] = color.b;
    }
    geom.attributes.color.needsUpdate = true;

    if (particlesRef.current.material) {
      const pulse = 0.5 + 0.3 * Math.sin(time * 2 + entropy.shannon * Math.PI);
      particlesRef.current.material.size = glow ? pulse : 0.3;
    }

    g.rotation.x += delta * 0.15 * (chaoticSpeedPct / 100);
    g.rotation.y += delta * 0.25 * (chaoticSpeedPct / 100);
    g.rotation.z += delta * 0.1 * (chaoticSpeedPct / 100);

    if (chaos) {
      g.position.x = Math.sin(time * 2) * 2.0;
      g.position.y = Math.cos(time * 1.5) * 2.0;
    } else {
      g.position.set(0, 0, 0);
    }

    // animation des éclairs
    if (lightningGeoms[0] && lightningPaths[0]) {
      lightningPaths.forEach((path, idx) => {
        path.forEach((p) => {
          p.x += (Math.random() - 0.5) * 1.2;
          p.y += (Math.random() - 0.5) * 1.2;
          p.z += (Math.random() - 0.5) * 1.2;
        });
        if (lightningGeoms[idx]) {
          lightningGeoms[idx].setPoints(path.map((pt) => [pt.x, pt.y, pt.z]).flat());
        }
      });
      if (lightningHaloGeom && lightningPaths[0]) {
        lightningHaloGeom.setPoints(lightningPaths[0].map((p) => [p.x, p.y, p.z]).flat());
      }
      const elapsed = performance.now() - (lightningStartRef.current || 0);
      const fade = Math.max(0, 1 - elapsed / 400);
      setLightningOpacity(fade);
    }
  });

  return (
    <>
      <group ref={groupRef}>
        {/* Particules */}
        <points ref={particlesRef} key={particleCount}>
          <bufferGeometry>
            <bufferAttribute attach="attributes-position" count={particleCount} array={positions} itemSize={3} />
            <bufferAttribute attach="attributes-color" count={particleCount} array={baseColors} itemSize={3} />
            <bufferAttribute attach="attributes-alpha" count={particleCount} array={alphas} itemSize={1} />
          </bufferGeometry>
          <pointsMaterial
            size={0.5}
            vertexColors
            transparent
            opacity={1.0}
            alphaTest={0.1}
            blending={THREE.AdditiveBlending}
            depthWrite={false}
          />
        </points>

        {/* ⚡ Éclairs */}
        {lightningGeoms[0] && (
          <mesh geometry={lightningGeoms[0]}>
            <primitive
              object={new MeshLineMaterial({
                color: new THREE.Color("#ff2222"),
                lineWidth: 0.05 + lightningIntensity * 0.2,
                transparent: true,
                opacity: lightningOpacity,
                depthTest: false,
                blending: THREE.AdditiveBlending,
              })}
              attach="material"
            />
          </mesh>
        )}
        {lightningGeoms[1] && (
          <mesh geometry={lightningGeoms[1]}>
            <primitive
              object={new MeshLineMaterial({
                color: new THREE.Color("#2299ff"),
                lineWidth: 0.02 + lightningIntensity * 0.1,
                transparent: true,
                opacity: lightningOpacity,
                depthTest: false,
                blending: THREE.AdditiveBlending,
              })}
              attach="material"
            />
          </mesh>
        )}
        {lightningHaloGeom && (
          <mesh geometry={lightningHaloGeom}>
            <primitive
              object={new MeshLineMaterial({
                color: new THREE.Color("#66ccff"),
                lineWidth: 0.05 + lightningIntensity * 0.3,
                transparent: true,
                opacity: lightningOpacity * 0.25,
                depthTest: false,
                blending: THREE.AdditiveBlending,
              })}
              attach="material"
            />
          </mesh>
        )}
      </group>

      <EffectComposer>
        <Bloom intensity={bloomIntensity} radius={bloomRadius} threshold={bloomThreshold} />
      </EffectComposer>
    </>
  );
}

export default function MetaCubeOracleV5(props) {
  return (
    <div id="metacube-oracle-v5-3d">
      <Canvas camera={{ position: [0, 40, 120], fov: 70 }}>
        <ambientLight intensity={0.6} />
        <pointLight position={[20, 20, 20]} intensity={1.4} />
        <MetaCubeOracleV5Core {...props} />
        <OrbitControls enableZoom enablePan />
      </Canvas>
    </div>
  );
}
